/**
 * Profile Settings Tab - User profile, avatar management, and password change.
 * Visible to all authenticated users.
 */

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { Camera, Trash2, User } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StoredImage } from '../../components/ui/StoredImage';
import { useAuthStore } from '../../stores/authStore';
import fileService from '../../services/api/fileService';

// ─── Max upload size: 5 MB ────────────────────────────────────────────────────
const MAX_FILE_BYTES = 5 * 1024 * 1024;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.charAt(0) ?? '';
  const l = lastName?.charAt(0) ?? '';
  return `${f}${l}`.toUpperCase() || '?';
};

// ─── Styled components ────────────────────────────────────────────────────────

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary.base};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const FormCard = styled(Card)`
  max-width: 600px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const RolesBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;


const SuccessMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  color: ${({ theme }) => theme.colors.status.error.text};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

// ─── Avatar section ───────────────────────────────────────────────────────────

const AvatarCard = styled(Card)`
  max-width: 600px;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const AvatarInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  flex: 1;
`;

const AvatarName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AvatarEmail = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AvatarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const AvatarHint = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin: ${({ theme }) => theme.spacing[3]} 0 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const ProfileSettingsTab = () => {
  const { user, refreshUser } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Avatar state ──
  const [avatarError, setAvatarError] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState('');

  // ── Upload mutation ──
  const uploadMutation = useMutation({
    mutationFn: (file: File) => fileService.uploadProfilePicture(file),
    onSuccess: async () => {
      await refreshUser();
      setAvatarError('');
      setAvatarSuccess('Profile photo updated successfully');
      setTimeout(() => setAvatarSuccess(''), 5000);
    },
    onError: (error: Error) => {
      setAvatarError(error.message || 'Failed to upload photo');
      setAvatarSuccess('');
    },
  });

  // ── Delete mutation ──
  const deleteMutation = useMutation({
    mutationFn: () => fileService.deleteProfilePicture(),
    onSuccess: async () => {
      await refreshUser();
      setAvatarError('');
      setAvatarSuccess('Profile photo removed');
      setTimeout(() => setAvatarSuccess(''), 5000);
    },
    onError: (error: Error) => {
      setAvatarError(error.message || 'Failed to remove photo');
    },
  });

  // ── Handlers ──

  const handleUploadClick = () => {
    setAvatarError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected after removal
    e.target.value = '';

    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      setAvatarError('File must be smaller than 5 MB');
      return;
    }

    uploadMutation.mutate(file);
  };

  const handleRemovePhoto = () => {
    setAvatarError('');
    deleteMutation.mutate();
  };

  const isAvatarBusy = uploadMutation.isPending || deleteMutation.isPending;
  const initials = getInitials(user?.firstName, user?.lastName);

  return (
    <Section>
      {/* ── Profile Photo ──────────────────────────────────────────────── */}
      <SectionBlock>
        <SectionHeader>
          <SectionIcon>
            <Camera />
          </SectionIcon>
          <SectionTitle>Profile Photo</SectionTitle>
        </SectionHeader>

        {avatarSuccess && <SuccessMessage>{avatarSuccess}</SuccessMessage>}
        {avatarError && <ErrorMessage>{avatarError}</ErrorMessage>}

        <AvatarCard padding="lg">
          <AvatarRow>
            <StoredImage
              src={user?.profilePic}
              alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Profile photo'}
              size="xl"
              fallback={initials}
              aria-label={`${user?.firstName} ${user?.lastName} avatar`}
            />
            <AvatarInfo>
              <AvatarName>
                {user?.firstName} {user?.lastName}
              </AvatarName>
              <AvatarEmail>{user?.email}</AvatarEmail>
            </AvatarInfo>
          </AvatarRow>

          <AvatarActions>
            <Button
              variant="outlined"
              size="sm"
              leftIcon={<Camera size={16} />}
              onClick={handleUploadClick}
              disabled={isAvatarBusy}
              isLoading={uploadMutation.isPending}
              aria-label="Upload a profile photo"
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload Photo'}
            </Button>

            {user?.profilePic && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 size={16} />}
                onClick={handleRemovePhoto}
                disabled={isAvatarBusy}
                isLoading={deleteMutation.isPending}
                aria-label="Remove profile photo"
              >
                {deleteMutation.isPending ? 'Removing…' : 'Remove Photo'}
              </Button>
            )}
          </AvatarActions>

          <AvatarHint>
            Accepted formats: JPG, PNG, GIF, WebP · Max size: 5 MB
          </AvatarHint>

          {/* Hidden file input — triggered programmatically */}
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            aria-hidden="true"
            tabIndex={-1}
          />
        </AvatarCard>
      </SectionBlock>

      {/* ── Profile Information ─────────────────────────────────────────── */}
      <SectionBlock>
        <SectionHeader>
          <SectionIcon>
            <User />
          </SectionIcon>
          <SectionTitle>Profile Information</SectionTitle>
        </SectionHeader>

        <FormCard padding="lg">
          <Form>
            <ReadOnlyField>
              <FieldLabel>Full Name</FieldLabel>
              <FieldValue>
                {user?.firstName} {user?.lastName}
              </FieldValue>
            </ReadOnlyField>

            <ReadOnlyField>
              <FieldLabel>Email Address</FieldLabel>
              <FieldValue>{user?.email}</FieldValue>
            </ReadOnlyField>

            <ReadOnlyField>
              <FieldLabel>Organization</FieldLabel>
              <FieldValue>{user?.organizationName || 'N/A'}</FieldValue>
            </ReadOnlyField>

            <ReadOnlyField>
              <FieldLabel>Status</FieldLabel>
              <RolesBadges>
                <Badge variant={user?.status === 'active' ? 'success' : 'neutral'}>
                  {user?.status || 'Unknown'}
                </Badge>
                {user?.isSuperAdmin && <Badge variant="info">Super Admin</Badge>}
              </RolesBadges>
            </ReadOnlyField>

            {user?.roles && user.roles.length > 0 && (
              <ReadOnlyField>
                <FieldLabel>Roles</FieldLabel>
                <RolesBadges>
                  {user.roles.map((role) => (
                    <Badge key={role} roleName={role}>
                      {role}
                    </Badge>
                  ))}
                </RolesBadges>
              </ReadOnlyField>
            )}
          </Form>
        </FormCard>
      </SectionBlock>

    </Section>
  );
};

export default ProfileSettingsTab;
